import { pathOr } from 'ramda';

import type {
  PartFileViewerPageData,
  PartViewerPageData,
  TechnicalDataPackage,
} from '@frontend/api';

import type { ChatId } from 'modules/common/chat/data/types';

import { PAGE_REQUEST, type AppAction } from 'store/app/actions';
import type { RootState } from 'store/configureStore';
import type { ActionOfType } from 'store/types';

import { types, type ChatSharesAction } from './actions';
import { CHANNEL_SETTINGS_MODAL_TABS } from './constants';
import type { ChannelSettingsModalTab } from './types';

type ChatSharesState = {
  activeTab: ChannelSettingsModalTab;
  areSharesCreating: boolean;
  channelId: ChatId | null;
  isChannelSettingsModalOpen: boolean;
  isChannelSettingsSaving: boolean;
  isCreatingChannel: boolean;
  isFetchingData: boolean;
  /** Written on DELETE_SHARES_FOR_CHANNEL_SUCCESS but never read */
  isMscChannelSettingsModalOpen?: boolean;
  isNewChannelModalOpen: boolean;
  isRevokingAccessToChannel: boolean;
  isTrustedThirdPartyChannelSettingsModalOpen: boolean;
  /** ISO 8601 expiration (or `null` for none), keyed by share id */
  shareExpirationChanges: Record<string, string | null>;
  /** Reset to `{}` while the new channel modal fetches */
  technicalDataPackage: TechnicalDataPackage | Record<string, never> | null;
};

const initialState: ChatSharesState = {
  activeTab: CHANNEL_SETTINGS_MODAL_TABS.SETTINGS,
  areSharesCreating: false,
  channelId: null,
  isChannelSettingsModalOpen: false,
  isChannelSettingsSaving: false,
  isCreatingChannel: false,
  isFetchingData: false,
  isNewChannelModalOpen: false,
  isRevokingAccessToChannel: false,
  isTrustedThirdPartyChannelSettingsModalOpen: false,
  shareExpirationChanges: {},
  technicalDataPackage: null,
};

type CombinedAction =
  | ChatSharesAction
  | ActionOfType<AppAction, typeof PAGE_REQUEST>;

const reducer = (
  state: ChatSharesState = initialState,
  action: CombinedAction,
): ChatSharesState => {
  switch (action.type) {
    case PAGE_REQUEST:
      return {
        ...initialState,
      };
    case types.CHANNEL_SETTINGS_CHANGE_EXPIRATION:
      return {
        ...state,
        shareExpirationChanges: {
          ...state.shareExpirationChanges,
          [action.payload.shareId]: action.payload.expirationDate,
        },
      };
    case types.CHANNEL_SETTINGS_MODAL_CLOSE:
      return {
        ...state,
        isChannelSettingsModalOpen: false,
      };
    case types.CHANNEL_SETTINGS_MODAL_OPEN:
      return {
        ...state,
        activeTab: action.payload.tab
          ? action.payload.tab
          : CHANNEL_SETTINGS_MODAL_TABS.SETTINGS,
        channelId: action.payload.channelId,
        isChannelSettingsModalOpen: true,
        isFetchingData: true,
        shareExpirationChanges: {},
      };
    case types.CHANNEL_SETTINGS_MODAL_SET_TAB:
      return {
        ...state,
        activeTab: action.payload.tabKey,
      };
    case types.CREATE_CHANNEL_ERROR:
      return {
        ...state,
        isCreatingChannel: false,
      };
    case types.CREATE_CHANNEL_REQUEST:
      return {
        ...state,
        isCreatingChannel: true,
      };
    case types.CREATE_CHANNEL_SUCCESS:
      return {
        ...state,
        isCreatingChannel: false,
        isNewChannelModalOpen: false,
      };
    case types.CREATE_SHARES_ERROR:
      return {
        ...state,
        areSharesCreating: false,
      };
    case types.CREATE_SHARES_REQUEST:
      return {
        ...state,
        areSharesCreating: true,
      };
    case types.CREATE_SHARES_SUCCESS:
      return {
        ...state,
        activeTab: CHANNEL_SETTINGS_MODAL_TABS.SETTINGS,
        areSharesCreating: false,
      };
    case types.DELETE_SHARES_FOR_CHANNEL_ERROR:
      return {
        ...state,
        isRevokingAccessToChannel: false,
      };
    case types.DELETE_SHARES_FOR_CHANNEL_REQUEST:
      return {
        ...state,
        isRevokingAccessToChannel: true,
      };
    case types.DELETE_SHARES_FOR_CHANNEL_SUCCESS:
      return {
        ...state,
        isChannelSettingsModalOpen: false,
        isMscChannelSettingsModalOpen: false,
        isRevokingAccessToChannel: false,
      };
    case types.INIT_SHARE_SETTINGS_ERROR:
      return {
        ...initialState,
      };
    case types.INIT_SHARE_SETTINGS_SUCCESS:
      return {
        ...state,
        isFetchingData: false,
        technicalDataPackage: action.payload.technicalDataPackage,
      };
    case types.NEW_CHANNEL_MODAL_CLOSE:
      return {
        ...state,
        isNewChannelModalOpen: false,
      };
    case types.NEW_CHANNEL_MODAL_OPEN:
      return {
        ...state,
        isFetchingData: true,
        isNewChannelModalOpen: true,
        technicalDataPackage: {},
      };
    case types.TRUSTED_THIRD_PARTY_CHANNEL_SETTINGS_MODAL_CLOSE:
      return {
        ...state,
        isTrustedThirdPartyChannelSettingsModalOpen: false,
      };
    case types.TRUSTED_THIRD_PARTY_CHANNEL_SETTINGS_MODAL_OPEN:
      return {
        ...state,
        channelId: action.payload.channelId,
        isFetchingData: true,
        isTrustedThirdPartyChannelSettingsModalOpen: true,
      };
    case types.UPDATE_CHANNEL_SETTINGS_ERROR:
      return {
        ...state,
        isChannelSettingsSaving: false,
      };
    case types.UPDATE_CHANNEL_SETTINGS_REQUEST:
      return {
        ...state,
        isChannelSettingsSaving: true,
      };
    case types.UPDATE_CHANNEL_SETTINGS_SUCCESS:
      return {
        ...state,
        isChannelSettingsSaving: false,
      };
    default:
      return state;
  }
};

export default reducer;

const getScope = (state: RootState): ChatSharesState =>
  state.modules.common.chat.shares;

export const areSharesCreating = (state: RootState) =>
  getScope(state).areSharesCreating;

export const getActiveTab = (state: RootState) => getScope(state).activeTab;

export const getTechnicalDataPackage = (state: RootState) =>
  getScope(state).technicalDataPackage;

/**
 * Gather primary files for display in share settings. Only want to show the
 * primary files that are not associated with an initial assembly upload
 * @param state
 * @returns {*[]}
 */
export const getPrimaryFiles = (state: RootState) => {
  const partInformation = pathOr<PartViewerPageData[]>(
    [],
    ['partInformation'],
    getTechnicalDataPackage(state),
  );
  const primaryFiles: PartFileViewerPageData[] = [];
  for (const part of partInformation) {
    if (!part.isManual) {
      const primaryFile = part.partFiles.find((pf) => pf.isPrimaryFile);
      if (
        primaryFile &&
        (part.isRootPart || !primaryFile.hasAssemblyParentFile)
      ) {
        primaryFiles.push(primaryFile);
      }
    }
  }
  return primaryFiles;
};

export const getChannelId = (state: RootState) => getScope(state).channelId;

export const getSupportingFiles = (state: RootState) => {
  const partInformation = pathOr<PartViewerPageData[]>(
    [],
    ['partInformation'],
    getTechnicalDataPackage(state),
  );
  let supportingFiles: PartFileViewerPageData[] = [];
  for (const part of partInformation) {
    supportingFiles = supportingFiles.concat(
      part.partFiles.filter((partFile) => !partFile.isPrimaryFile),
    );
  }
  return supportingFiles;
};

export const isChannelSettingsModalOpen = (state: RootState) =>
  getScope(state).isChannelSettingsModalOpen;

export const isChannelSettingsSaving = (state: RootState) =>
  getScope(state).isChannelSettingsSaving;

export const isCreatingChannel = (state: RootState) =>
  getScope(state).isCreatingChannel;

export const isFetchingData = (state: RootState) =>
  getScope(state).isFetchingData;

export const isTrustedThirdPartyChannelSettingsModalOpen = (state: RootState) =>
  getScope(state).isTrustedThirdPartyChannelSettingsModalOpen;

export const isNewChannelModalOpen = (state: RootState) =>
  getScope(state).isNewChannelModalOpen;

export const isRevokingAccessToChannel = (state: RootState) =>
  getScope(state).isRevokingAccessToChannel;

export const getShareExpirationChanges = (state: RootState) =>
  getScope(state).shareExpirationChanges;
